import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import * as Mui from "@mui/material";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { sharedTableBodyCellSx, sharedTableHeadCellSx } from "components/DesignSystem/sharedUiPatterns";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { Loader } from "components/Loader";
import { designColors, designType } from "services/constants/designTokens";
import { tooltipStyleProps } from "services/constants/Styles";
import { customGetAPIQuiet } from "services/utils/CustomAPI";
import { contactInitials } from "pages/Administration/SupportContacts/supportContactsData";
import { mapOrganisationContact, organisationContactsUrl } from "pages/Administration/Organisation/organisationContactsApi";
import { CONTACT_TYPE_COLORS } from "pages/Administration/Organisation/organisationContactsRules";

// Primary contacts first, then the rest in the order the API returns them.
const byContactType = (a, b) => (a.contactType === "Primary" ? 0 : 1) - (b.contactType === "Primary" ? 0 : 1);

const headCellSx = { ...sharedTableHeadCellSx, position: "static", whiteSpace: "nowrap" };
const bodyCellSx = { ...sharedTableBodyCellSx };
const columns = [
  // Shares of the panel width, so the table fits without a sideways
  // scroll; long values end in "…" and show in full in the shared tooltip.
  { key: "name", label: "Name", width: "28%" },
  { key: "phone", label: "Phone Number", width: "24%" },
  { key: "email", label: "Email Address", width: "30%" },
  { key: "contactType", label: "Contact Type", width: "18%" },
];

const cell = (key, contact) => {
  switch (key) {
    case "name":
      return (
        <Mui.Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <Mui.Avatar sx={{ width: 28, height: 28, fontSize: 12, fontWeight: 600, bgcolor: designColors.tealTint, color: designColors.tealDark }}>
            {contactInitials(contact.name)}
          </Mui.Avatar>
          <Mui.Tooltip title={contact.name} arrow slotProps={tooltipStyleProps}>
            <Mui.Typography noWrap sx={{ fontSize: 14, fontWeight: 500, minWidth: 0 }}>{contact.name}</Mui.Typography>
          </Mui.Tooltip>
        </Mui.Stack>
      );
    case "contactType":
      return contact.contactType ? <RequestStatusChip status={contact.contactType} colorCode={CONTACT_TYPE_COLORS[contact.contactType]} /> : "—";
    default:
      return contact[key] ? (
        <Mui.Tooltip title={contact[key]} arrow slotProps={tooltipStyleProps}>
          <Mui.Box component="span" sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {contact[key]}
          </Mui.Box>
        </Mui.Tooltip>
      ) : "—";
  }
};

// The organisation's contacts as a table, same columns as the
// Administration > Organisation > Contacts list.
const ContactsTable = ({ contacts }) => (
  <Mui.TableContainer sx={{ border: "1px solid " + designColors.slate200, borderRadius: 2, overflowX: "hidden" }}>
    <Mui.Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
      <Mui.TableHead>
        <Mui.TableRow>
          {columns.map(({ key, label, width }) => (
            <Mui.TableCell key={key} sx={{ ...headCellSx, width }}>
              {label}
            </Mui.TableCell>
          ))}
        </Mui.TableRow>
      </Mui.TableHead>
      <Mui.TableBody>
        {contacts.map((contact) => (
          <Mui.TableRow key={contact.id} sx={{ "&:last-child td": { borderBottom: "none" } }}>
            {columns.map(({ key }) => (
              <Mui.TableCell key={key} sx={bodyCellSx}>
                {cell(key, contact)}
              </Mui.TableCell>
            ))}
          </Mui.TableRow>
        ))}
      </Mui.TableBody>
    </Mui.Table>
  </Mui.TableContainer>
);

ContactsTable.propTypes = { contacts: PropTypes.arrayOf(PropTypes.object).isRequired };

// Side peek opened by "Contact Administrator": the organisation's contacts
// from the Administration > Organisation > Contacts API, loaded each time
// it opens.
export const AdministratorContactsPanel = ({ open, onClose }) => {
  const { domain } = useSelector((state) => state.tokenReducer);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const retry = useCallback(() => setReloadKey((key) => key + 1), []);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoading(true);
    setFailed(false);
    customGetAPIQuiet(organisationContactsUrl(domain)).then((result) => {
      if (!active) return;
      if (result?.ok && Array.isArray(result.responseDetails)) {
        setContacts(result.responseDetails.map(mapOrganisationContact).sort(byContactType));
      } else {
        setFailed(true);
      }
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [open, domain, reloadKey]);

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="Contact Administrator"
      subtitle="Reach your organisation's contact persons for workspace access."
      bodySx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}
    >
      {loading ? (
        <Loader info="Loading..." minHeight="30vh" />
      ) : failed ? (
        <Mui.Alert severity="error" action={<Mui.Button color="inherit" size="small" onClick={retry}>Retry</Mui.Button>}>
          Contact details could not be loaded.
        </Mui.Alert>
      ) : contacts.length === 0 ? (
        <Mui.Typography sx={{ ...designType.helperText, textAlign: "center", py: 4 }}>
          No contact details have been added for your organisation yet.
        </Mui.Typography>
      ) : (
        <ContactsTable contacts={contacts} />
      )}
    </GlobalSidePanel>
  );
};

AdministratorContactsPanel.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
